import React, { useEffect, useState } from 'react';
import { getMyStore } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, Spinner, Badge } from '../../components/shared/UI';

const MAX = 30;

export default function ClientDashboard() {
  const { user }  = useAuth();
  const [store, setStore]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // GET api/client/store → { config, productos }
    getMyStore().then(setStore).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'flex',justifyContent:'center',padding:60 }}><Spinner /></div>
  );
  if (!store) return null;

  const productos = store.productos || [];
  const config    = store.config    || {};
  const featured  = productos.filter(p => p.destacado).length;
  const pct       = Math.round((productos.length / MAX) * 100);
  const storeLink = `${window.location.origin}/tienda/${user.clientId}`;

  // Calcular estado de suscripción del usuario actual
  const isExpired = user.expiresAt && new Date(user.expiresAt) < new Date();
  const isInactive = user.active === false;

  return (
    <div>
      {/* Subscription warning banner */}
      {(isExpired || isInactive) && (
        <div style={{
          background:'rgba(239,68,68,0.12)',border:'1px solid rgba(239,68,68,0.3)',
          borderRadius:14,padding:'16px 20px',marginBottom:22,
          display:'flex',alignItems:'flex-start',gap:12,
        }}>
          <span style={{ fontSize:22,flexShrink:0 }}>⚠️</span>
          <div>
            <div style={{ color:'#fca5a5',fontWeight:700,fontSize:14,marginBottom:4 }}>
              {isExpired ? 'Suscripción vencida' : 'Tienda inactiva'}
            </div>
            <div style={{ color:'rgba(252,165,165,0.75)',fontSize:13,lineHeight:1.5 }}>
              Web impaga. Si no pagás la mensualidad tendrás que abonar <strong>$30.000 + $20.000 de activación.</strong>
              <br />Contactá al administrador para renovar tu suscripción.
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom:26 }}>
        <h1 style={{ color:'#fff',fontSize:26,fontFamily:"'Syne',sans-serif",fontWeight:800,margin:'0 0 6px' }}>
          ¡Hola, {user?.username}! 👋
        </h1>
        <p style={{ color:'rgba(255,255,255,0.38)',fontSize:14,margin:0 }}>Resumen de tu tienda online.</p>
      </div>

      {/* Stats */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:14,marginBottom:22 }}>
        {[
          { icon:'📦', label:'Productos',  value:productos.length, sub:`/${MAX}` },
          { icon:'⭐', label:'Destacados', value:featured, sub:'' },
          { icon:'💰', label:'Moneda',     value:config.moneda || 'ARS', sub:'' },
        ].map((s, i) => (
          <div key={i} style={{ background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:14,padding:'18px 20px' }}>
            <div style={{ fontSize:20,marginBottom:8 }}>{s.icon}</div>
            <div style={{ color:'rgba(255,255,255,0.38)',fontSize:11,fontWeight:500,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:4 }}>{s.label}</div>
            <div style={{ display:'flex',alignItems:'baseline',gap:3 }}>
              <span style={{ color:'#fff',fontSize:26,fontWeight:700,fontFamily:"'Syne',sans-serif",lineHeight:1 }}>{s.value}</span>
              {s.sub && <span style={{ color:'rgba(255,255,255,0.3)',fontSize:12 }}>{s.sub}</span>}
            </div>
          </div>
        ))}
        {/* Color swatch */}
        <div style={{ background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:14,padding:'18px 20px' }}>
          <div style={{ fontSize:20,marginBottom:8 }}>🎨</div>
          <div style={{ color:'rgba(255,255,255,0.38)',fontSize:11,fontWeight:500,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8 }}>Color</div>
          <div style={{ display:'flex',alignItems:'center',gap:8 }}>
            <div style={{ width:22,height:22,borderRadius:6,background:config.color||'#7c3aed',border:'2px solid rgba(255,255,255,0.2)' }} />
            <span style={{ color:'rgba(255,255,255,0.4)',fontSize:12 }}>{config.color||'#7c3aed'}</span>
          </div>
        </div>
      </div>

      {/* Product capacity bar */}
      <Card style={{ marginBottom:16 }}>
        <div style={{ display:'flex',justifyContent:'space-between',marginBottom:10 }}>
          <span style={{ color:'#fff',fontSize:14,fontWeight:500 }}>Capacidad de productos</span>
          <span style={{ color: pct >= 90 ? '#fca5a5' : 'rgba(255,255,255,0.5)',fontSize:13,fontWeight:600 }}>
            {productos.length}/{MAX}
          </span>
        </div>
        <div style={{ height:8,background:'rgba(255,255,255,0.08)',borderRadius:4,overflow:'hidden' }}>
          <div style={{
            height:'100%',borderRadius:4,
            width:`${Math.min(100, pct)}%`,
            transition:'width 0.5s ease',
            background: pct >= 90
              ? 'linear-gradient(90deg,#ef4444,#dc2626)'
              : 'linear-gradient(90deg,#7c3aed,#4f46e5)',
          }} />
        </div>
        {pct >= 90 && (
          <p style={{ color:'#fca5a5',fontSize:12,marginTop:8,margin:'8px 0 0' }}>
            ⚠️ Estás cerca del límite. Eliminá productos para agregar nuevos.
          </p>
        )}
      </Card>

      {/* Store link */}
      <Card style={{ background:'rgba(124,58,237,0.08)',borderColor:'rgba(124,58,237,0.22)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12 }}>
          <div>
            <div style={{ color:'#c4b5fd',fontSize:13,fontWeight:500,marginBottom:4 }}>🔗 Link de tu tienda</div>
            <div style={{ color:'rgba(255,255,255,0.45)',fontSize:12,wordBreak:'break-all' }}>{storeLink}</div>
          </div>
          <a
            href={storeLink}
            target="_blank"
            rel="noreferrer"
            style={{
              padding:'9px 18px',background:'rgba(124,58,237,0.28)',
              border:'1px solid rgba(124,58,237,0.4)',borderRadius:10,
              color:'#c4b5fd',fontSize:13,fontWeight:600,textDecoration:'none',whiteSpace:'nowrap',
            }}
          >
            Ver tienda →
          </a>
        </div>
      </Card>
    </div>
  );
}
